/**
 * Editing a definition: add, remove, reconfigure, re-place.
 *
 * PURE, like `filters.ts`, and for the same reason — placement has awkward
 * cases (where does a new tile go when the grid is full of half-height ones?)
 * that are worth asserting about directly rather than through a component.
 */
import type {
  DashboardDefinition,
  DashboardTile,
  ReportSpec,
  TileLayout,
  VizKind,
} from '@clockwise/semantic-core';
import { DASHBOARD_COLUMNS, MAX_TILES } from '@clockwise/semantic-core';
import type { Layout } from 'react-grid-layout';

/**
 * A new report tile's size, by what it draws.
 *
 * A TABLE is full width: its columns are the content, and at half width a
 * pivot with a month across the top loses most of them behind the tile's edge.
 * A bar, line or area chart is half width, so two sit side by side; a pie needs
 * little width and three fit a row. All of them are tall enough for a chart's
 * axes plus a footer.
 */
export function reportTileSize(viz: VizKind): Pick<TileLayout, 'w' | 'h'> {
  switch (viz) {
    case 'table':
      return { w: 24, h: 10 };
    case 'pie':
      return { w: 8, h: 10 };
    case 'metric':
      return METRIC_TREND_TILE;
    default:
      return { w: 12, h: 10 };
  }
}
/**
 * A KPI is one number: a sixth of the width, so six sit in a row, and very
 * little height.
 */
const METRIC_TILE: Pick<TileLayout, 'w' | 'h'> = { w: 4, h: 3 };
/**
 * With a sparkline under it, the same number needs room for the shape.
 *
 * FOUR ROWS (~168px) against ~148px of content: header, figure with its
 * period, the change line, and a 36px sparkline. It fits because a dashboard
 * metric no longer carries the two lines that once set this at six — the
 * measure's name (the header's ⓘ names it: `measureNamed`) and the row-count
 * footer (`ReportFooter summary={false}`).
 *
 * The ~20px of slack is for the optional lines that can still appear: a filter
 * notice, or a warning footer on a capped or unsplittable result. Those are
 * SIBLINGS of the view in `DataTile`'s flex column and keep their height; when
 * they push past the slack it is the metric block (the bottom of its sparkline)
 * that gives way, never a warning. Lower than four would start eating the
 * sparkline with no notice present at all.
 */
const METRIC_TREND_TILE: Pick<TileLayout, 'w' | 'h'> = { w: 4, h: 4 };
/** A note is full-width by default — it is usually a section heading. */
const TEXT_TILE: Pick<TileLayout, 'w' | 'h'> = { w: 24, h: 3 };

export const tileFull = (tiles: readonly DashboardTile[]) => tiles.length >= MAX_TILES;

/**
 * Where a new tile goes: the first row below everything already placed.
 *
 * BELOW rather than in the first gap. A gap-filling placement drops the tile
 * somewhere the author is not looking — they pressed a button at the top of
 * the page and the thing they added appeared in the middle of the grid — and
 * the grid will compact it upward on its own anyway once they move something.
 */
export function nextPosition(
  tiles: readonly DashboardTile[],
  size: Pick<TileLayout, 'w' | 'h'>,
): TileLayout {
  const bottom = tiles.reduce((max, t) => Math.max(max, t.layout.y + t.layout.h), 0);
  return { x: 0, y: bottom, w: Math.min(size.w, DASHBOARD_COLUMNS), h: size.h };
}

/**
 * A tile id that is unique within this dashboard.
 *
 * Derived from a counter over the existing ids rather than random, so a
 * definition is reproducible in a test and readable in the audit log — and so
 * two tiles added in one session cannot collide.
 */
export function nextTileId(tiles: readonly DashboardTile[], prefix: string): string {
  const taken = new Set(tiles.map((t) => t.id));
  for (let n = 1; ; n++) {
    const id = `${prefix}-${n}`;
    if (!taken.has(id)) return id;
  }
}

/** Put a tile back as the server now has it — after "Save as custom report". */
export function replaceTile(
  definition: DashboardDefinition,
  tile: DashboardTile,
): DashboardDefinition {
  return {
    ...definition,
    tiles: definition.tiles.map((t) => (t.id === tile.id ? tile : t)),
  };
}

/** What the note panel edits: the heading, and the text under it. */
export interface NoteSettings {
  title: string;
  body: string;
}

export function addTextTile(
  definition: DashboardDefinition,
  note: NoteSettings = { title: '', body: '' },
): DashboardDefinition {
  const title = note.title.trim();
  const tile: DashboardTile = {
    id: nextTileId(definition.tiles, 'text'),
    type: 'text',
    layout: nextPosition(definition.tiles, TEXT_TILE),
    ...(title ? { title } : {}),
    body: note.body,
  };
  return { ...definition, tiles: [...definition.tiles, tile] };
}

/**
 * Apply the note panel: a new note (`id` null) or the one being configured.
 * One write, like the report panel's Apply — title and text land together.
 */
export function applyNoteSettings(
  definition: DashboardDefinition,
  id: string | null,
  note: NoteSettings,
): DashboardDefinition {
  if (id === null) return addTextTile(definition, note);
  const title = note.title.trim();
  return {
    ...definition,
    tiles: definition.tiles.map((t) =>
      // Only the note: a blank title is stored absent, as `setTileTitle` does.
      t.id === id && t.type === 'text'
        ? { ...t, title: title === '' ? undefined : title, body: note.body }
        : t,
    ),
  };
}

/**
 * One number, composed on the dashboard.
 *
 * Carries its spec INLINE rather than referencing a saved report, because
 * "total billable hours, big, at the top" is not a report anybody wants
 * cluttering their reports list. `viz: 'metric'` is the one shape
 * `reportSpecSchema` lets have no row dimension.
 *
 * The window stored here is the dashboard's, and `effectiveSpec` replaces it on
 * every run — it is written only because a spec is not valid without one.
 */
export function addMetricTile(
  definition: DashboardDefinition,
  model: string,
  measure: string,
  title?: string,
  /**
   * A time dimension to trend over, when the model has one.
   *
   * Present, the tile is a number with its history: the figure is the
   * dashboard's window, the comparison is that window's mirror (the quarter
   * before a quarter), and the series is the sparkline across both. Absent, it
   * is the number over the dashboard's whole window and there is nothing to
   * compare it to.
   */
  trend?: string,
): DashboardDefinition {
  const spec: ReportSpec = {
    model,
    kind: 'pivot',
    period: definition.period.preset,
    from: definition.period.from,
    to: definition.period.to,
    measures: [measure],
    rows: trend ? [trend] : [],
    cols: [],
    columns: [],
    filters: [],
    rowSort: [],
    colSort: [],
    showAs: {},
    valueFormat: {},
    totals: { row: false, col: false },
    suppressRepeats: [],
    subtotals: [],
    viz: 'metric',
    stacked: false,
  };
  const tile: DashboardTile = {
    id: nextTileId(definition.tiles, 'metric'),
    type: 'metric',
    layout: nextPosition(definition.tiles, trend ? METRIC_TREND_TILE : METRIC_TILE),
    title,
    spec,
  };
  return { ...definition, tiles: [...definition.tiles, tile] };
}

export function removeTile(definition: DashboardDefinition, id: string): DashboardDefinition {
  return { ...definition, tiles: definition.tiles.filter((t) => t.id !== id) };
}

export function setTileTitle(
  definition: DashboardDefinition,
  id: string,
  title: string,
): DashboardDefinition {
  const trimmed = title.trim();
  return {
    ...definition,
    tiles: definition.tiles.map((t) =>
      // Empty means "use the report's own name" — stored as absent rather than
      // as an empty string, so a cleared box falls back instead of rendering
      // a blank header.
      t.id === id ? { ...t, title: trimmed === '' ? undefined : trimmed } : t,
    ),
  };
}

export function setTileBody(
  definition: DashboardDefinition,
  id: string,
  body: string,
): DashboardDefinition {
  return {
    ...definition,
    tiles: definition.tiles.map((t) => (t.id === id && t.type === 'text' ? { ...t, body } : t)),
  };
}

/** Fold the grid's positions back onto the tiles they belong to. */
export function withLayout(definition: DashboardDefinition, layout: Layout): DashboardDefinition {
  const byId = new Map(layout.map((l) => [l.i, l]));
  return {
    ...definition,
    tiles: definition.tiles.map((tile) => {
      const at = byId.get(tile.id);
      return at ? { ...tile, layout: { x: at.x, y: at.y, w: at.w, h: at.h } } : tile;
    }),
  };
}

/** The grid's view of a definition. */
export const toLayout = (definition: DashboardDefinition): Layout =>
  definition.tiles.map((t) => ({ i: t.id, ...t.layout }));

/** Positions only — RGL adds resolved defaults (`moved`, `static`, …). */
export function sameLayout(a: Layout, b: Layout): boolean {
  if (a.length !== b.length) return false;
  return a.every((item) => {
    const other = b.find((l) => l.i === item.i);
    return (
      !!other &&
      other.x === item.x &&
      other.y === item.y &&
      other.w === item.w &&
      other.h === item.h
    );
  });
}
